
import { annotationSourceReference } from '@/utils/annotationSource';
import Vue, { PropType } from 'vue';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import {
	RunnitNodeField,
	IMAGE_UPLOAD_MODE,
	RunnitNodeRunInputValue,
	RunnitNodeRunResult,
	RunnitUpload,
} from '@run-diffusion/shared';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import RunnitSelectedImage from '@/views/Runnits/RunnitSelectedImage.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import _cloneDeep from 'lodash/cloneDeep';
import ComplexBackgroundBaseStyledMenu from '@/components/designElements/ComplexBackgroundBaseStyledMenu.vue';
import { TeamUserMixin } from '@/mixins';
import VisualPromptEditor from '@/views/Runnits/RunnitSettings/VisualPromptEditor/VisualPromptEditor.vue';
import Pose3DEditor from '@/views/Runnits/RunnitSettings/Pose3DEditor/Pose3DEditor.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { fetchAsset3DAsFile } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFile';
import type { ImgsVisualPromptReplaceTarget } from '@/utils/imgsVisualPromptTarget';
import {
	buildOriginalImagesMap,
	originalEntryForTarget,
	originalImageSourceFromEntry,
	replaceImgsValueAtTarget,
	resetImgsValueAtTarget,
} from '@/utils/imgsVisualPromptTarget';

export default Vue.extend({
	name: 'MultiImageInputField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: Object,
			required: false,
		},
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
			IMAGE_GALLERY_DIALOG_NAV,
			annotateSessionImgsSnapshot: null as RunnitNodeRunInputValue | null,
			showUse3DSources: false,
		};
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	watch: {
		use3DSelectionStateForTrigger: {
			immediate: false,
			handler (newVal) {
				// The 3D picker never appends to IMGS: a chosen model is only a source
				// for the editor, and the capture is what becomes the image.
				const nodeRunResult = this._get(newVal, 'selectedNodeRunResults[0]') || null;
				const upload = this._get(newVal, 'selectedUploads[0]') || null;
				if (!nodeRunResult && !upload) return;
				this.openPose3DEditorFromLibrary({ nodeRunResult, upload });
			},
		},
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				const committedSelection = this.getCommittedMultiSelectionState(newVal, oldVal, this.field.uuid);
				if (!committedSelection) return;

				const { selectedNodeRunResults, selectedUploads } = committedSelection;

				this.onInput({
					uploads: selectedUploads,
					nodeRunResults: selectedNodeRunResults,
				});
			},
		},
		annotateSessionOpenForField (isOpen: boolean, wasOpen: boolean) {
			if (isOpen && !wasOpen) {
				this.annotateSessionImgsSnapshot = _cloneDeep(this.value);
			}
		},
		visualPromptResetRequest (target) {
			if (!target) return;
			// Clear first so a re-render can't replay it, then reset without opening the editor.
			this.updateVisualPromptEditorState({
				fieldUuid: this.field.uuid,
				editorState: { resetToOriginalRequest: null },
			});
			this.resetImgsTarget(target, { refreshOpenEditor: false });
		},
	},
	computed: {
		...mapState(['runnitState']),
		...mapGetters(['getInpaintingEditorState']),
		visualPromptResetRequest () {
			return this.getVisualPromptEditorState[this.field.uuid]?.resetToOriginalRequest || null;
		},
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		getPose3DEditorState () {
			const states = this.runnitState.pose3DEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						assetFile: null,
						assetName: null,
					};
				},
			});
		},
		selectionStateTriggerId () {
			return `multi-image-input-${this.field.uuid}`;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
		isAtCapacity (): boolean {
			return !!this.maxImages && this.allImages.length >= this.maxImages;
		},
		atCapacityMessage (): string {
			return `Maximum of ${this.maxImages} images allowed`;
		},
		use3DSelectionStateTriggerId () {
			return `multi-image-input-3d-${this.field.uuid}`;
		},
		use3DSelectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.use3DSelectionStateTriggerId);
		},
		maxImages (): number {
			return this._get(this.field, 'display.maxImages') || null;
		},
		hasImages () {
			return !!(this.allImages && this.allImages.length > 0);
		},
		allImages () {
			const uploads = this._get(this.value, 'uploads') || [];
			const nodeRunResults = this._get(this.value, 'nodeRunResults') || [];
			return [
				...uploads.map(upload => ({ upload })),
				...nodeRunResults.map(nodeRunResult => ({ nodeRunResult })),
			];
		},
		displayedImages () {
			// Show up to 3 images in the stack
			return this.allImages.slice(0, 3);
		},
		remainingCount () {
			return Math.max(0, this.allImages.length - 3);
		},
		visualPromptOriginalImage () {
			const state = this.getVisualPromptEditorState[this.field.uuid];
			if (!state || !state.replaceImageTarget) {
				return null;
			}
			return originalEntryForTarget(this.value, state.replaceImageTarget);
		},
		annotateSessionOpenForField (): boolean {
			return !!this.getVisualPromptEditorState[this.field.uuid]?.open;
		},
	},
	methods: {
		getStackStyle (index: number) {
			// Index 0 is front, Index 1 behind, etc.
			// Z-index: Highest for 0
			const zIndex = 10 - index;
			// Offset: 0 for 0, 6px for 1, 12px for 2
			const offset = index * 6;

			return {
				zIndex: zIndex,
				bottom: `${offset}px`,
				left: `${offset}px`,
			};
		},
		...mapActions([
			'updateRunnitState',
			'updateVisualPromptEditorState',
			'updatePose3DEditorState',
			'updateSnackbar',
		]),
		triggerUpload (close: Function) {
			this.$refs.imageUpload.openFileDialog();
			close();
		},
		/**
		 * @param explicit — use after $emit when `this.value` may still reflect the prior prop until parent re-renders.
		 */
		syncSelectionState (explicit?: { uploads: RunnitUpload[]; nodeRunResults: RunnitNodeRunResult[]; originalImages?: any }) {
			const currentUploads = explicit
				? explicit.uploads
				: (this._get(this.value, 'uploads') || []);
			const currentNodeRunResults = explicit
				? explicit.nodeRunResults
				: (this._get(this.value, 'nodeRunResults') || []);
			const originalImages = explicit && explicit.originalImages !== undefined
				? explicit.originalImages
				: (this._get(this.value, 'originalImages') || {});

			this.syncImgsSelectionState({
				triggeringId: this.selectionStateTriggerId,
				fieldAwaitingImageGallerySelection: this.field,
				uploads: currentUploads,
				nodeRunResults: currentNodeRunResults,
				enterMode: true,
				originalImages,
			});
		},
		openSelectedImagesDialog (close?: Function) {
			this.syncSelectionState();
			this.updateRunnitState({ selectedImagesDialogOpen: true });
			if (close) {
				close();
			}
		},
		openLibrary (imageGalleryDialogInitialNav: ImageGalleryDialogNav, close: Function) {
			this.syncSelectionState();
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			close();
		},
		clearImages (close: Function) {
			this.$emit('input', null);
			this.clearSelectionsFromState(this.selectionStateTriggerId);
			close();
		},
		handleImageUpload (items: any[]) {
			// Filter out null items
			const validItems = items.filter(item => !!item);

			const newUploads = [];
			const newNodeRunResults = [];

			validItems.forEach(item => {
				if (item.nodeRunResult) {
					// It's a mapped nodeRunResult from drag and drop
					newNodeRunResults.push(item.nodeRunResult);
				} else if (item.upload) {
					// It's a mapped upload from drag and drop
					newUploads.push(item.upload);
				} else {
					// It's a raw UploadFile (RunnitUpload) from file selection/drop
					newUploads.push(item);
				}
			});

			const currentUploads = this._get(this.value, 'uploads') || [];
			const currentNodeRunResults = this._get(this.value, 'nodeRunResults') || [];

			let combinedUploads = [...currentUploads, ...newUploads];
			let combinedNodeRunResults = [...currentNodeRunResults, ...newNodeRunResults];

			if (this.maxImages) {
				const totalCount = combinedUploads.length + combinedNodeRunResults.length;
				if (totalCount > this.maxImages) {
					// Create a ordered list of all images to truncate correctly
					// Note: this maintains the existing order (uploads then results) which matches allImages computed prop
					const allImages = [
						...combinedUploads.map(u => ({ type: 'upload', data: u })),
						...combinedNodeRunResults.map(r => ({ type: 'result', data: r })),
					];

					const truncated = allImages.slice(0, this.maxImages);
					combinedUploads = truncated.filter(i => i.type === 'upload').map(i => i.data);
					combinedNodeRunResults = truncated.filter(i => i.type === 'result').map(i => i.data);
				}
			}

			this.onInput({
				uploads: combinedUploads,
				nodeRunResults: combinedNodeRunResults,
			});

			this.clearAllSelectionStates();
		},
		onPose3DCapture (imageValue: { upload?: RunnitUpload }) {
			if (!imageValue || !imageValue.upload) return;
			this.handleImageUpload([{ upload: imageValue.upload }]);
		},
		// The cap is checked when a source is chosen rather than when the submenu
		// opens, so the message names the action the user actually took.
		isAtImageCap (close: Function): boolean {
			if (this.isAtCapacity) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.atCapacityMessage,
					show: true,
				});
				this.showUse3DSources = false;
				close();
				return true;
			}
			return false;
		},
		toggleUse3DSources () {
			this.showUse3DSources = !this.showUse3DSources;
		},
		triggerUse3DUpload (close: Function) {
			if (this.isAtImageCap(close)) return;
			const input = this.$refs.pose3dFileInput as HTMLInputElement | undefined;
			if (input) input.click();
			this.showUse3DSources = false;
			close();
		},
		openUse3DLibrary (close: Function) {
			if (this.isAtImageCap(close)) return;
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
			});
			// A separate trigger id from the image picker so a chosen model opens the
			// editor instead of being appended to this field's IMGS array.
			this.enterSelectionMode(
				NODE_RUN_RESULT_SELECTION_MODE.SINGLE,
				this.use3DSelectionStateTriggerId,
				this.field,
				['ASSET_3D'],
			);
			this.showUse3DSources = false;
			close();
		},
		async openPose3DEditorFromLibrary (source: { nodeRunResult?: any, upload?: any }) {
			try {
				const file: File = await fetchAsset3DAsFile(source);
				this.updatePose3DEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						open: true,
						saving: false,
						assetFile: file,
						assetName: file.name,
					},
				});
			} catch (err: any) {
				console.error('Failed to open 3D asset from library', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: err && err.message ? err.message : 'Could not open that 3D asset',
					show: true,
				});
			} finally {
				this.clearSelectionsFromState(this.use3DSelectionStateTriggerId);
				this.exitSelectionMode(this.use3DSelectionStateTriggerId);
			}
		},
		onPose3DFileSelected (ev: Event) {
			const target = ev.target as HTMLInputElement;
			const file = target.files && target.files[0];
			target.value = '';
			if (!file) return;
			this.updatePose3DEditorState({
				fieldUuid: this.field.uuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
		onInput (value: any) {
			if (value && (value.uploads?.length > 0 || value.nodeRunResults?.length > 0)) {
				const uploads = value.uploads || [];
				const nodeRunResults = value.nodeRunResults || [];
				const existingOriginalImages = value.originalImages || this._get(this.value, 'originalImages') || {};
				const originalImages = buildOriginalImagesMap(uploads, nodeRunResults, existingOriginalImages);
				this.$emit('input', {
					...value,
					originalImages,
				});
			} else {
				this.$emit('input', null);
			}
		},
		forwardDragEvent (method, event) {
			if (this.$refs.imageUpload && typeof this.$refs.imageUpload[method] === 'function') {
				this.$refs.imageUpload[method](event);
			}
		},
		async onVisualPromptResetToOriginal () {
			const target = this.getVisualPromptEditorState[this.field.uuid]?.replaceImageTarget;
			if (!target) return;
			await this.resetImgsTarget(target, { refreshOpenEditor: true });
		},
		/**
		 * Reset one image to its original. Shared by the in-editor Reset button
		 * (refreshOpenEditor: shows the restored original in the still-open editor) and the
		 * Selected Images dialog's per-tile Reset (no editor involved).
		 */
		async resetImgsTarget (target, { refreshOpenEditor }: { refreshOpenEditor: boolean }) {
			const next = resetImgsValueAtTarget(this.value, target);
			if (!next) return;

			this.onInput({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			await this.$nextTick();
			this.syncSelectionState({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			if (!refreshOpenEditor) return;
			const source = originalImageSourceFromEntry(next.original);
			if (!source) return;
			const baseSource = annotationSourceReference(source.source, source.useCase);
			if (!baseSource) {
				this.updateVisualPromptEditorState({ fieldUuid: this.field.uuid, editorState: { open: false, backgroundImageUrl: null, baseSource: null } });
				return;
			}
			const { imageUrl } = await this.fetchSrc(source.source, source.useCase);
			this.updateVisualPromptEditorState({
				fieldUuid: this.field.uuid,
				editorState: {
					open: true,
					backgroundImageUrl: imageUrl,
					baseSource,
				},
			});
		},
		onVisualPromptAnnotateCancel () {
			if (this.annotateSessionImgsSnapshot == null) return;
			const snap = _cloneDeep(this.annotateSessionImgsSnapshot);
			this.onInput(snap);
			this.$nextTick(() => {
				this.syncSelectionState({
					uploads: this._get(snap, 'uploads') || [],
					nodeRunResults: this._get(snap, 'nodeRunResults') || [],
					originalImages: this._get(snap, 'originalImages') || {},
				});
			});
			this.annotateSessionImgsSnapshot = null;
		},
		onVisualPromptAnnotateSubmitDismissed () {
			this.annotateSessionImgsSnapshot = null;
		},
		handleImageReplacement ({ upload, replaceImageTarget }: { upload: RunnitUpload, replaceImageTarget: ImgsVisualPromptReplaceTarget }) {
			const next = replaceImgsValueAtTarget(this.value, replaceImageTarget, upload);
			if (!next) return;

			this.onInput({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			this.$nextTick(() => {
				this.syncSelectionState({
					uploads: next.uploads,
					nodeRunResults: next.nodeRunResults,
					originalImages: next.originalImages,
				});
				this.updateVisualPromptEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						completedReplacement: {
							upload,
							replaceImageTarget,
						},
					},
				});
				this.annotateSessionImgsSnapshot = null;
			});
		},
	},
	components: {
		MenuOrBottomSheet,
		ImageUpload,
		RunnitSelectedImage,
		ComplexBackgroundBaseStyledMenu,
		VisualPromptEditor,
		Pose3DEditor,
	},
});
